import React from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/ui/Section';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiX, FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import Button from '../components/ui/Button';
import Contact from '../components/sections/Contact';

// Referenzprojekt Mittelring 13 (Inhalte: Textdokument Stand 17.06.2026).
const project = {
  title: 'Mittelring 13, Leipzig',
  intro:
    'Ein Vorzeigeprojekt für modernes, energieeffizientes und zukunftssicheres Wohnen im urbanen Raum. Die hier eingesetzte serielle Präzision nutzen wir eins zu eins auch für Ihr individuelles Eigenheim.',
  leipzig: {
    heading: 'Leipzig – Dynamisch und Wachstumsstark',
    paragraphs: [
      'Historisch als bedeutende Handels- und Messestadt gewachsen, verbindet die Stadt Leipzig auf einzigartige Weise Tradition mit zukunftsorientierter Entwicklung. Ihre zentrale Lage und die hervorragende Infrastruktur machen sie zum attraktiven Magneten für Unternehmen und Fachkräfte.',
      'Als wirtschaftliches Zentrum Mitteldeutschlands bietet Leipzig einen vielseitigen Branchenmix: Neben der traditionsreichen Messe prägen die Automobilindustrie (mit den Werken von Porsche und BMW), Logistik, Life Sciences, IT sowie Energie- und Umwelttechnik das Profil. Der Sektor Forschung und Entwicklung wächst kontinuierlich durch die enge Vernetzung von Wirtschaft, Universität und Wissenschaft. Zudem gilt Leipzig mit dem MDR, Verlagen und Kreativagenturen als wichtigstes Medienzentrum Ostdeutschlands.',
      'Die starke wirtschaftliche Dynamik und das anhaltende Bevölkerungswachstum der Stadt Leipzig sichern eine konstant hohe Nachfrage nach Wohnraum – mit anhaltend hoher Wohnungsnachfrage in innenstadtnahen Lagen.',
    ],
  },
  facts: [
    ['Standort', 'Mittelring 13, 04158 Leipzig (Stadtteil Sellerhausen / Schönefeld-Abtnaundorf)'],
    ['Objekttyp', 'Mehrfamilienhaus in serieller, industrieller Modulbauweise (KfW-Effizienzhaus 40)'],
    ['Wohneinheiten', '11 Wohneinheiten (1- bis 3-Zimmer), Erdgeschosswohnungen barrierefrei, alle mit Balkon und Kellerabteil'],
    ['Gesamtfläche', '621,36 m² Gesamtwohnfläche (einzelne Wohnungen 36,21 bis 77,60 m²)'],
    ['Leistungsumfang', 'Ganzheitlich: Konzeption, Beratung, serielle Umsetzung'],
  ],
  units: [
    ['WE 59', '2', '43,18 m²', '243.967 €'],
    ['WE 60', '2', '44,97 m²', '254.080,50 €'],
    ['WE 61', '2', '44,97 m²', '254.080,50 €'],
    ['WE 62', '2', '43,16 m²', '243.854 €'],
    ['WE 63', '3', '75,75 m²', '427.987,50 €'],
    ['WE 64', '3', '75,75 m²', '427.987,50 €'],
    ['WE 65', '1', '37,83 m²', '213.739,50 €'],
    ['WE 66', '3', '70,92 m²', '400.698 €'],
    ['WE 67', '3', '70,92 m²', '400.698 €'],
    ['WE 68', '1', '36,21 m²', '204.586,50 €'],
    ['WE 69', '3', '77,6 m²', '438.440 €'],
  ],
  floorplans: [
    ['/images/grundriss-eg.webp', 'Erdgeschoss', 'EG'],
    ['/images/grundriss-1og.webp', '1. Obergeschoss', '1. OG'],
    ['/images/grundriss-2og.webp', '2. Obergeschoss', '2. OG'],
    ['/images/grundriss-dg.webp', 'Dachgeschoss', 'DG'],
  ],
  objectFeatures: [
    '11 hochwertig ausgestattete Neubauwohnungen',
    'Energiestandard KfW 40',
    'Alle Wohnungen mit Balkon',
    'Wohnungen im EG barrierefrei',
    'Parkettboden und moderne Feinsteinzeugfliesen',
    '1- bis 3-Zimmer, ca. 36 bis ca. 78 m²',
  ],
  challenge:
    'Genau hier galt es, zeitgemäßen, energieeffizienten Wohnraum zu schaffen, der drei Anforderungen zugleich erfüllt: planbare Bauzeiten, wirtschaftliche Umsetzung und die strengen Vorgaben des KfW-40-Standards. Herkömmliche Baumethoden bieten in puncto Kosten- und Terminsicherheit zu viele Risiken. Gesucht war eine Lösung, die das Projekt von der Konzeption bis zur schlüsselfertigen Übergabe absichert und gleichzeitig eine solide Grundlage für eine langfristig stabile Vermietung schafft.',
  solutionIntro:
    'Ab der Kellerdecke wurde das Gebäude konsequent in industrieller, serieller Modulbauweise errichtet: Die oberirdischen Geschosse bestehen aus vorgefertigten Raummodulen bzw. großformatigen Bauelementen, die unter kontrollierten Bedingungen industriell hergestellt und auf der Baustelle montiert werden. Dieser hohe Vorfertigungsgrad sichert eine präzise, qualitativ hochwertige Umsetzung und spürbar verkürzte Bauzeiten gegenüber dem konventionellen Hochbau.',
  solutionPoints: [
    ['Ganzheitlicher Ansatz', 'Bereits in der Konzeptionsphase wurde die serielle Fertigung der Wand- und Deckenmodule mitgedacht und auf die Vorgaben von Energieberater und Architekt abgestimmt – das schuf von Beginn an klar strukturierte, funktionale Grundrisse.'],
    ['Finanzierung auf solidem Fundament', 'Der durchgängige KfW-40-Standard und die hohe Planungssicherheit schaffen attraktive Rahmenbedingungen für Kapitalanleger – von der KfW-Förderfähigkeit bis hin zur 5-prozentigen degressiven Abschreibung für Neubauten, die gerade in den ersten Jahren für eine spürbare steuerliche Entlastung sorgt.'],
    ['Effiziente Umsetzung', 'Die standardisierten, aber flexibel konfigurierbaren Modultypen erlaubten eine hocheffiziente Baustellenlogistik. Ergänzt wird das Energiekonzept durch eine Wärmepumpe mit Fußbodenheizung sowie eine Photovoltaikanlage auf dem Dach zur regenerativen Stromerzeugung.'],
  ],
  quote:
    'Dank der seriellen Vorfertigung war das Projekt ein voller Erfolg und setzt beim Thema Nachhaltigkeit und Energieeffizienz neue Maßstäbe für zeitgemäßes Wohnen.',
  quoteAuthor: 'Statement eines zufriedenen Eigentümers',
  highlights: [
    ['Punktlandung beim Budget', 'Hohe Kosten- und Terminsicherheit durch industrielle Vorfertigung – präzise, wirtschaftliche Umsetzung bei optimierten Bauzeiten.'],
    ['Nachhaltigkeit in Serie', 'Erreichung des strengen KfW-40-Standards durch optimierten Materialeinsatz, ein regeneratives Heizsystem (Wärmepumpe) und eine dachseitige Photovoltaikanlage.'],
    ['Werterhalt mit Zukunft', 'Hochwertige Ausstattung (Parkett- bzw. Vinyl-Designbelag, moderne Feinsteinzeugfliesen, Tiefgarage) in innenstadtnaher, gefragter Lage mit langfristigem Wertpotenzial und minimalen Instandhaltungskosten in den ersten Jahrzehnten.'],
  ],
  nearby: [
    'Einkaufsmöglichkeiten für den täglichen Bedarf in der Nähe',
    'Klinikum St. Georg Leipzig, Allgemeinmediziner, Apotheke',
    'Nähe zum Volkspark Abtnaundorf sowie weiteren Grün- und Erholungsflächen',
    'Schulen und Kindertagesstätten befinden sich im direkten Umfeld',
  ],
};

const FloorplanTabs = ({ plans }) => {
  const [active, setActive] = React.useState(0);
  const [lightbox, setLightbox] = React.useState(false);
  const [zoom, setZoom] = React.useState(false);
  const [origin, setOrigin] = React.useState('50% 50%');
  const triggerRef = React.useRef(null);
  const closeRef = React.useRef(null);

  const go = (dir) => {
    setActive((i) => (i + dir + plans.length) % plans.length);
    setZoom(false);
    setOrigin('50% 50%');
  };

  // Zoom nur am Desktop (mobil: nativer Pinch-Zoom)
  const isDesktop = () =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
  const toggleZoom = () => {
    if (!isDesktop()) return;
    setZoom((z) => {
      if (z) setOrigin('50% 50%');
      return !z;
    });
  };
  const onPan = (e) => {
    if (!zoom) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  React.useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, plans.length]);

  const close = () => {
    setLightbox(false);
    setZoom(false);
    setOrigin('50% 50%');
    triggerRef.current?.focus();
  };

  const [src, label] = plans[active];

  return (
    <div className="border-t border-brand-text/20 pt-8 mb-16">
      {/* Tab-Leiste — auf Mobile horizontal scrollbar, keine Umbrüche */}
      <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-1 px-1 pb-1 mb-6">
        {plans.map(([, full, short], idx) => {
          const isActive = idx === active;
          return (
            <button
              key={full}
              type="button"
              onClick={() => setActive(idx)}
              aria-pressed={isActive}
              className={`shrink-0 font-mono text-[11px] sm:text-xs uppercase tracking-widest px-4 py-2.5 border transition-colors duration-200 ${
                isActive
                  ? 'bg-brand-text text-brand-bg border-brand-text'
                  : 'bg-transparent text-brand-text/50 border-brand-text/20 hover:text-brand-text hover:border-brand-text/50'
              }`}
            >
              <span className="sm:hidden">{short}</span>
              <span className="hidden sm:inline">{full}</span>
            </button>
          );
        })}
      </div>

      {/* Aktiver Grundriss — Karte im Sitehintergrund, mit Pfeilen + Lightbox */}
      <div className="relative bg-brand-bg border border-brand-text/10 group">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setLightbox(true)}
          aria-label={`Grundriss ${label} vergrößern`}
          className="block w-full p-4 md:p-8 cursor-zoom-in"
        >
          <img
            src={src}
            alt={`Grundriss ${label}`}
            loading="lazy"
            className="w-full h-auto max-h-[80vh] object-contain mx-auto mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.01]"
          />
        </button>

        {/* Blättern direkt im Block */}
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Vorheriges Geschoss"
          className="absolute top-1/2 -translate-y-1/2 left-2 md:left-3 p-2 md:p-2.5 bg-brand-bg/85 border border-brand-text/15 text-brand-text/60 hover:text-brand-text hover:border-brand-text/40 transition-colors"
        >
          <FiChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Nächstes Geschoss"
          className="absolute top-1/2 -translate-y-1/2 right-2 md:right-3 p-2 md:p-2.5 bg-brand-bg/85 border border-brand-text/15 text-brand-text/60 hover:text-brand-text hover:border-brand-text/40 transition-colors"
        >
          <FiChevronRight size={20} />
        </button>
      </div>

      {/* Lightbox — überlagert die Seite */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Grundriss ${label}`}
          onClick={close}
          className="fixed inset-0 z-50 bg-brand-text/95 flex flex-col animate-[fadeIn_.2s_ease-out]"
        >
          {/* Kopfzeile: Etikett + Zähler + Schließen */}
          <div
            className="flex items-center justify-between p-4 md:p-6 text-brand-bg"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-widest">
              {label} · {active + 1} / {plans.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleZoom}
                aria-label={zoom ? 'Verkleinern' : 'Vergrößern'}
                aria-pressed={zoom}
                className="hidden md:inline-flex p-2 hover:opacity-60 transition-opacity"
              >
                {zoom ? <FiZoomOut size={22} /> : <FiZoomIn size={22} />}
              </button>
              <button
                ref={closeRef}
                type="button"
                onClick={close}
                aria-label="Schließen"
                className="p-2 hover:opacity-60 transition-opacity"
              >
                <FiX size={24} />
              </button>
            </div>
          </div>

          {/* Plan mit Navigationspfeilen */}
          <div
            className="flex-1 flex items-center justify-center gap-2 md:gap-4 px-2 md:px-8 pb-8 min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Vorheriges Geschoss"
              className="shrink-0 p-2 md:p-3 text-brand-bg border border-brand-bg/30 hover:bg-brand-bg hover:text-brand-text transition-colors"
            >
              <FiChevronLeft size={24} />
            </button>

            <div className="bg-brand-bg p-3 md:p-6 max-h-full flex items-center overflow-hidden">
              <img
                src={src}
                alt={`Grundriss ${label}`}
                onClick={toggleZoom}
                onMouseMove={onPan}
                draggable={false}
                style={{
                  transform: zoom ? 'scale(2)' : 'scale(1)',
                  transformOrigin: origin,
                  transition: 'transform .15s ease-out',
                }}
                className={`max-w-full max-h-[80vh] w-auto h-auto object-contain mix-blend-multiply select-none ${
                  zoom ? 'md:cursor-zoom-out' : 'md:cursor-zoom-in'
                }`}
              />
            </div>

            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Nächstes Geschoss"
              className="shrink-0 p-2 md:p-3 text-brand-bg border border-brand-bg/30 hover:bg-brand-bg hover:text-brand-text transition-colors"
            >
              <FiChevronRight size={24} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectDetail = () => {
  return (
    <div className="pt-24 min-h-screen">
      <Section className="pb-12">
        <Link
          to="/"
          state={{ scrollTo: 'portfolio' }}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase hover:opacity-50 transition-opacity mb-12"
        >
          <SafeIcon icon={FiArrowLeft} /> Zurück zur Übersicht
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-12">
          <h1 className="text-5xl md:text-7xl lg:text-8xl leading-[0.85]">{project.title}</h1>
          <p className="font-inter text-xl text-brand-text/70 max-w-md pb-2">{project.intro}</p>
        </div>

        {/* Repräsentatives Projektbild */}
        <div className="group w-full aspect-[21/9] overflow-hidden mb-16">
          <img
            src="/images/mittelring-hero.webp"
            alt="Mittelring 13, Leipzig — Außenansicht des Mehrfamilienhauses"
            className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out"
          />
        </div>

        {/* Einleitung: Leipzig als Standort */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <h2 className="lg:col-span-4 text-3xl md:text-4xl leading-[0.95]">{project.leipzig.heading}</h2>
          <div className="lg:col-span-8 space-y-6">
            {project.leipzig.paragraphs.map((p, idx) => (
              <p key={idx} className="font-inter text-lg text-brand-text/80 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>

        {/* Daten & Fakten */}
        <h2 className="font-mono text-xs uppercase text-brand-text/50 mb-6 tracking-widest">Das Projekt im Überblick</h2>
        <div className="border-t border-brand-text/20 mb-16">
          {project.facts.map(([label, value]) => (
            <div key={label} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-8 py-5 border-b border-brand-text/10 font-inter">
              <div className="md:col-span-4 font-mono text-xs uppercase tracking-widest text-brand-text/50">{label}</div>
              <div className="md:col-span-8 text-brand-text/90">{value}</div>
            </div>
          ))}
        </div>

        {/* Übersicht Einheiten */}
        <h2 className="font-mono text-xs uppercase text-brand-text/50 mb-6 tracking-widest">Übersicht Einheiten</h2>
        <div className="overflow-x-auto border-t border-brand-text/20 mb-16">
          <table className="w-full min-w-[19rem] border-collapse font-inter">
            <thead>
              <tr className="border-b border-brand-text/20">
                <th className="text-left font-mono text-[10px] sm:text-xs uppercase tracking-widest text-brand-text/50 py-4 pr-3">Einheit</th>
                <th className="text-right font-mono text-[10px] sm:text-xs uppercase tracking-widest text-brand-text/50 py-4 px-2">Zimmer</th>
                <th className="text-right font-mono text-[10px] sm:text-xs uppercase tracking-widest text-brand-text/50 py-4 px-2">Größe</th>
                <th className="text-right font-mono text-[10px] sm:text-xs uppercase tracking-widest text-brand-text/50 py-4 pl-2">Preis</th>
              </tr>
            </thead>
            <tbody>
              {project.units.map(([einheit, zimmer, groesse, preis]) => (
                <tr key={einheit} className="border-b border-brand-text/10">
                  <td className="py-3.5 pr-3 text-brand-text/80 whitespace-nowrap text-sm sm:text-base">{einheit}</td>
                  <td className="py-3.5 px-2 text-right text-brand-text/80 tabular-nums text-sm sm:text-base">{zimmer}</td>
                  <td className="py-3.5 px-2 text-right text-brand-text/80 tabular-nums whitespace-nowrap text-sm sm:text-base">{groesse}</td>
                  <td className="py-3.5 pl-2 text-right text-brand-text/80 tabular-nums whitespace-nowrap text-sm sm:text-base">{preis}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Grundrisse */}
        <h2 className="font-mono text-xs uppercase text-brand-text/50 mb-6 tracking-widest">Grundrisse</h2>
        <FloorplanTabs plans={project.floorplans} />

        {/* Objektmerkmale */}
        <h2 className="font-mono text-xs uppercase text-brand-text/50 mb-6 tracking-widest">Objektmerkmale</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 border-t border-brand-text/20 pt-8 mb-16">
          {project.objectFeatures.map((feature) => (
            <li key={feature} className="flex items-start gap-3 font-inter text-brand-text/90">
              <span className="mt-2 w-1.5 h-1.5 bg-brand-text shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-8 space-y-12">
            <div>
              <h3 className="text-2xl md:text-3xl mb-4">Herausforderung</h3>
              <p className="font-inter text-lg text-brand-text/80 leading-relaxed mb-8">{project.challenge}</p>
              <Button href="#contact" showArrow>Mehr Informationen anfordern</Button>
            </div>

            <div>
              <h3 className="text-2xl md:text-3xl mb-4">Unsere Lösung</h3>
              <p className="font-inter text-lg text-brand-text/80 leading-relaxed mb-8">{project.solutionIntro}</p>
              <ul className="space-y-6">
                {project.solutionPoints.map(([head, body]) => (
                  <li key={head} className="border-l-2 border-brand-text/30 pl-6">
                    <span className="font-medium text-brand-text">{head}: </span>
                    <span className="font-inter text-brand-text/80">{body}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="border-l-2 border-brand-text pl-6 py-2 lg:sticky lg:top-32">
              <p className="text-xl md:text-2xl font-inter italic text-brand-text/90 leading-relaxed mb-6">„{project.quote}"</p>
              <p className="font-mono text-xs uppercase tracking-widest text-brand-text/50">— {project.quoteAuthor}</p>
            </div>
          </aside>
        </div>

        {/* Highlights */}
        <div className="bg-brand-text/5 border border-brand-text/10 p-8 md:p-12 mb-16">
          <h3 className="font-mono text-xs uppercase tracking-widest text-brand-text/50 mb-8">Die Highlights des Projekts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {project.highlights.map(([head, body]) => (
              <div key={head} className="flex flex-col">
                <h4 className="text-lg mb-3 md:min-h-[3.5rem]">{head}</h4>
                <p className="font-inter text-sm text-brand-text/80 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bildergalerie */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16">
          <div className="group aspect-square overflow-hidden">
            <img src="/images/mittelring-1.webp" alt="Mittelring 13 — Ansicht 1" className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out" />
          </div>
          <div className="group aspect-square overflow-hidden">
            <img src="/images/mittelring-2.webp" alt="Mittelring 13 — Ansicht 2" className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out" />
          </div>
          <div className="group aspect-square overflow-hidden">
            <img src="/images/mittelring-3.webp" alt="Mittelring 13 — Ansicht 3" className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out" />
          </div>
        </div>

        {/* Lage & Umgebung */}
        <h2 className="font-mono text-xs uppercase text-brand-text/50 mb-6 tracking-widest">Lage</h2>
        <div className="border border-brand-text/10 overflow-hidden mb-12">
          <iframe
            title="Lage Mittelring 13, 04158 Leipzig"
            src="https://www.google.com/maps?q=Mittelring+13,+04158+Leipzig&output=embed"
            width="100%"
            height="400"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="block w-full"
          />
        </div>

        <h3 className="text-2xl md:text-3xl mb-6">Alles in der Nähe</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 border-t border-brand-text/20 pt-8">
          {project.nearby.map((item) => (
            <li key={item} className="flex items-start gap-3 font-inter text-brand-text/90">
              <span className="mt-2 w-1.5 h-1.5 bg-brand-text shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </Section>
      <Contact />
    </div>
  );
};

export default ProjectDetail;
